import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap";

import { useCompanies } from "../../../hooks/companies";
import SortHeader from "../../../components/SortHeader";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import TablePagination from "../../../components/TablePagination";
import { paginate, sortRows, toggleSort } from "../../../utils/tableHelpers";
import { rotuloDocumento } from "../../../utils/modeloDocumento";
import { criarFaturaManual, listarDocumentosDisponiveis } from "../../../services/faturaManualApi";

import {
  Aviso,
  CheckboxTodos,
  EstadoTabela,
  ModalHub,
  SelecaoResumo,
  ehConflito,
  extractError,
  fieldInputStyle,
  fmtBRL,
  fmtData,
  fmtNumber,
  periodoMesAtual,
  useRemetentes,
  useSelecao,
} from "../_documentosFrete/comum";
import "../_documentosFrete/styles.css";

// Nova fatura (PO1-3307, épico PO1-3298): quando a transportadora não manda
// fatura eletrônica (DOCCOB), o embarcador monta a fatura com os documentos (CT-e
// ou NFse) ainda não faturados do remetente × transportadora no período. Abre
// pelo botão "Nova fatura" da Auditoria de Faturas; ao criar, a tela recarrega a
// lista e abre a fatura nova (onCriada).

const PAGE_SIZES = [20, 50, 100];

// Status_Calculo do documento (1 = não calculado, 2 = OK, 3 = divergente).
const SITUACOES_CALCULO = { 1: "Não calculado", 2: "OK", 3: "Divergente" };
export const rotuloSituacao = (v) => (v === null || v === undefined || v === "" ? "-" : SITUACOES_CALCULO[Number(v)] || String(v));

const TABLE_COLUMNS = [
  { key: "documento", label: "Documento", sortField: "numero" },
  { key: "chave", label: "Chave" },
  { key: "datEmissao", label: "Emissão", sortField: "datEmissao" },
  { key: "valorFrete", label: "Valor cobrado", sortField: "valorFrete", numeric: true },
  { key: "valorSimulado", label: "Valor calculado", sortField: "valorSimulado", numeric: true },
  { key: "situacaoCalculo", label: "Situação", sortField: "situacaoCalculo" },
];

const filtrosIniciais = (transportadoraId) => ({
  idEmbarcadorRemetente: "",
  idTransportadora: transportadoraId ? String(transportadoraId) : "",
  ...periodoMesAtual(),
});

const formInicial = (periodo) => ({
  numero: "",
  dataEmissao: periodo.ate,
  dataVencimento: "",
  competenciaDe: periodo.de,
  competenciaAte: periodo.ate,
});

// `transportadoraPadrao`: a transportadora filtrada na Auditoria de Faturas, se houver.
export default function NovaFaturaModal({ onClose, onCriada, transportadoraPadrao }) {
  const { empresaId, hasEmpresaSelection, transportadoraId, optionsTransportadora } = useCompanies();
  const transportadoraInicial = transportadoraPadrao || transportadoraId;
  const remetentes = useRemetentes(hasEmpresaSelection ? empresaId : null);
  const transportadoras = optionsTransportadora || [];

  const [draft, setDraft] = useState(() => filtrosIniciais(transportadoraInicial));
  const [filtros, setFiltros] = useState(() => filtrosIniciais(transportadoraInicial));
  const [busca, setBusca] = useState("");
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [sort, setSort] = useState({ field: "datEmissao", dir: "asc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const selecao = useSelecao("pesoBruto", "valorFrete");

  const [form, setForm] = useState(() => formInicial(periodoMesAtual()));
  const [salvando, setSalvando] = useState(false);
  const salvandoRef = useRef(false); // trava o clique duplo antes do re-render
  const [erroCriar, setErroCriar] = useState("");

  const filtrosCompletos = Boolean(filtros.idEmbarcadorRemetente && filtros.idTransportadora);

  const carregar = useCallback(async () => {
    if (!filtrosCompletos) {
      setDocumentos([]);
      return;
    }
    setLoading(true);
    setErro("");
    try {
      setDocumentos(
        await listarDocumentosDisponiveis({
          idEmbarcadorRemetente: Number(filtros.idEmbarcadorRemetente),
          idTransportadora: Number(filtros.idTransportadora),
          de: filtros.de,
          ate: filtros.ate,
        })
      );
    } catch (e) {
      setDocumentos([]);
      setErro(extractError(e, "Não foi possível consultar os documentos disponíveis."));
    } finally {
      setLoading(false);
    }
  }, [filtros, filtrosCompletos]);

  useEffect(() => {
    if (!hasEmpresaSelection) return;
    carregar();
  }, [carregar, hasEmpresaSelection]);

  // Filtro novo: seleção limpa e a competência acompanha o período.
  useEffect(() => {
    setPage(1);
    selecao.limpar();
    setErroCriar("");
    setForm((atual) => ({ ...atual, competenciaDe: filtros.de, competenciaAte: filtros.ate, dataEmissao: atual.dataEmissao || filtros.ate }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros]);

  const updateDraft = (campo, valor) => setDraft((atual) => ({ ...atual, [campo]: valor }));
  const aplicar = () => {
    const base = periodoMesAtual();
    setFiltros({ ...draft, de: draft.de || base.de, ate: draft.ate || base.ate });
  };
  const setCampo = (campo, valor) => setForm((atual) => ({ ...atual, [campo]: valor }));

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return documentos;
    return documentos.filter((d) => [d.numero, d.chave, rotuloSituacao(d.situacaoCalculo)].some((v) => String(v || "").toLowerCase().includes(termo)));
  }, [busca, documentos]);
  const ordenados = useMemo(() => sortRows(filtrados, sort), [filtrados, sort]);
  const pagina = useMemo(() => paginate(ordenados, page, pageSize), [ordenados, page, pageSize]);
  useEffect(() => {
    setPage(1);
  }, [busca, sort, documentos]);

  const totais = useMemo(
    () =>
      filtrados.reduce(
        (acc, d) => ({
          cobrado: acc.cobrado + (Number(d.valorFrete) || 0),
          calculado: acc.calculado + (Number(d.valorSimulado) || 0),
          nfse: acc.nfse + (rotuloDocumento(d.modelo) === "NFse" ? 1 : 0),
        }),
        { cobrado: 0, calculado: 0, nfse: 0 }
      ),
    [filtrados]
  );

  const numeroOk = Boolean(form.numero.trim());
  const datasOk = Boolean(form.dataEmissao && form.dataVencimento && form.competenciaDe && form.competenciaAte);
  const competenciaOk = !datasOk || form.competenciaDe <= form.competenciaAte;
  const podeCriar = !salvando && selecao.ids.length > 0 && numeroOk && datasOk && competenciaOk && filtrosCompletos;

  const fechar = () => {
    if (salvandoRef.current) return;
    onClose();
  };

  const criar = async () => {
    if (salvandoRef.current || !podeCriar) return;
    salvandoRef.current = true;
    setSalvando(true);
    setErroCriar("");
    const numero = form.numero.trim();
    let criada = null;
    try {
      criada =
        (await criarFaturaManual({
          idEmbarcadorRemetente: Number(filtros.idEmbarcadorRemetente),
          idTransportadora: Number(filtros.idTransportadora),
          numero,
          dataEmissao: form.dataEmissao,
          dataVencimento: form.dataVencimento,
          competenciaDe: form.competenciaDe,
          competenciaAte: form.competenciaAte,
          idsDocumentos: selecao.ids,
        })) || {};
    } catch (e) {
      setErroCriar(
        ehConflito(e)
          ? extractError(e, "Já existe uma fatura com este número para a transportadora.")
          : extractError(e, "Não foi possível criar a fatura.")
      );
      salvandoRef.current = false;
      setSalvando(false);
      return;
    }
    salvandoRef.current = false;
    // A tela-mãe fecha o modal (desmonta), então não há estado a atualizar aqui.
    if (onCriada) onCriada({ ...criada, numero: criada.numero || numero });
  };

  if (!hasEmpresaSelection) {
    return (
      <ModalHub titulo="Nova fatura" icone="tim-icons icon-paper" onClose={fechar}>
        <SpecificEmpresaRequiredNotice message="Selecione um embarcador específico para criar a fatura." />
        <div className="docs-frete__modal-rodape">
          <Button color="secondary" size="sm" onClick={fechar}>
            Fechar
          </Button>
        </div>
      </ModalHub>
    );
  }

  return (
    <ModalHub titulo="Nova fatura" icone="tim-icons icon-paper" onClose={fechar} maxWidth={1080}>
      <div className="docs-frete docs-frete__form">
        <p className="docs-frete__secao" style={{ marginTop: 0 }}>
          Monte a fatura da transportadora com documentos (CT-e ou NFse) ainda não faturados do remetente no período. Use quando a transportadora não envia DOCCOB. A fatura criada segue para a Auditoria de Faturas.
        </p>
        <Row>
          <Col md="4">
            <FormGroup>
              <Label for="fatura-remetente">Remetente</Label>
              <Input id="fatura-remetente" type="select" value={draft.idEmbarcadorRemetente} onChange={(e) => updateDraft("idEmbarcadorRemetente", e.target.value)} style={fieldInputStyle}>
                <option value="">Selecione o remetente...</option>
                {remetentes.map((r) => (
                  <option key={r.Id} value={r.Id}>
                    {r.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label for="fatura-transportadora">Transportadora</Label>
              <Input id="fatura-transportadora" type="select" value={draft.idTransportadora} onChange={(e) => updateDraft("idTransportadora", e.target.value)} style={fieldInputStyle}>
                <option value="">Selecione a transportadora...</option>
                {transportadoras.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="fatura-de">Emissão de</Label>
              <Input id="fatura-de" type="date" value={draft.de} onChange={(e) => updateDraft("de", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="fatura-ate">Até</Label>
              <Input id="fatura-ate" type="date" value={draft.ate} onChange={(e) => updateDraft("ate", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="8">
            <FormGroup>
              <Label for="fatura-busca">Filtrar documentos</Label>
              <Input id="fatura-busca" type="text" placeholder="Número, chave ou situação" value={busca} onChange={(e) => setBusca(e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="4" className="docs-frete__acoes" style={{ alignItems: "flex-end", paddingBottom: "1rem" }}>
            <Button color="primary" size="sm" outline onClick={aplicar} disabled={loading}>
              {loading ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
              Buscar documentos
            </Button>
          </Col>
        </Row>

        <p className="docs-frete__secao">
          Documentos sem fatura · {fmtNumber(filtrados.length)} ({fmtNumber(totais.nfse)} NFse · {fmtNumber(filtrados.length - totais.nfse)} CT-e) · cobrado {fmtBRL(totais.cobrado)} · calculado{" "}
          {fmtBRL(totais.calculado)}
        </p>
        {selecao.totais.qtd > 0 ? (
          <SelecaoResumo totais={selecao.totais} rotulo="documento(s)" sufixo="selecionado(s)" semValor>
            <span>
              Valor cobrado <b>{fmtBRL(selecao.totais.valor)}</b>
            </span>
            <Button color="default" size="sm" outline onClick={selecao.limpar}>
              Desmarcar seleção
            </Button>
          </SelecaoResumo>
        ) : null}
        <EstadoTabela
          loading={loading}
          erro={erro}
          vazio={!loading && !ordenados.length}
          textoCarregando="Consultando documentos..."
          textoVazio={filtrosCompletos ? "Nenhum documento sem fatura para este remetente e transportadora no período." : "Escolha o remetente e a transportadora e clique em Buscar documentos."}
        />
        {!loading && !erro && ordenados.length ? (
          <>
            <Table className="docs-frete__tabela hub-thead-accent" size="sm" responsive hover>
              <thead className="text-primary">
                <tr>
                  <th className="is-sel">
                    <CheckboxTodos itens={pagina.items} selecao={selecao} label="Selecionar todos os documentos visíveis" />
                  </th>
                  {TABLE_COLUMNS.map((col) => (
                    <th key={col.key} className={col.numeric ? "is-number" : undefined}>
                      {col.sortField ? (
                        <SortHeader label={col.label} field={col.sortField} sort={sort} onSort={(f) => setSort((s) => toggleSort(s, f))} align={col.numeric ? "right" : "left"} />
                      ) : (
                        col.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagina.items.map((d) => (
                  <tr key={d.id}>
                    <td className="is-sel">
                      <input type="checkbox" aria-label={`Selecionar ${rotuloDocumento(d.modelo)} ${d.numero}`} checked={selecao.has(d.id)} onChange={() => selecao.toggle(d)} />
                    </td>
                    <td>
                      <strong>
                        {rotuloDocumento(d.modelo)} {d.numero}
                      </strong>
                      <small>
                        Série {d.serie || "-"}
                        {d.tipoDocumento ? ` · ${d.tipoDocumento}` : ""}
                      </small>
                    </td>
                    <td className="docs-frete__chave">{d.chave || "-"}</td>
                    <td className="docs-frete__nowrap">{fmtData(d.datEmissao)}</td>
                    <td className="is-number">{fmtBRL(d.valorFrete)}</td>
                    <td className="is-number">{d.valorSimulado != null ? fmtBRL(d.valorSimulado) : "-"}</td>
                    <td>{rotuloSituacao(d.situacaoCalculo)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <TablePagination
              page={pagina.page}
              pageSize={pageSize}
              total={pagina.total}
              pageSizes={PAGE_SIZES}
              itemLabel="documento(s)"
              onPageChange={setPage}
              onPageSizeChange={(novo) => {
                setPageSize(novo);
                setPage(1);
              }}
            />
          </>
        ) : null}

        <p className="docs-frete__secao">Dados da fatura · número da transportadora, datas e competência (padrão: o período filtrado)</p>
        <Row>
          <Col md="3">
            <FormGroup>
              <Label for="fatura-numero">Número da fatura</Label>
              <Input id="fatura-numero" type="text" maxLength={100} value={form.numero} onChange={(e) => setCampo("numero", e.target.value)} style={fieldInputStyle} disabled={salvando} />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label for="fatura-emissao">Data de emissão</Label>
              <Input id="fatura-emissao" type="date" value={form.dataEmissao} onChange={(e) => setCampo("dataEmissao", e.target.value)} style={fieldInputStyle} disabled={salvando} />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="fatura-vencimento">Vencimento</Label>
              <Input id="fatura-vencimento" type="date" value={form.dataVencimento} onChange={(e) => setCampo("dataVencimento", e.target.value)} style={fieldInputStyle} disabled={salvando} />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="fatura-competencia-de">Competência de</Label>
              <Input id="fatura-competencia-de" type="date" value={form.competenciaDe} onChange={(e) => setCampo("competenciaDe", e.target.value)} style={fieldInputStyle} disabled={salvando} />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="fatura-competencia-ate">Competência até</Label>
              <Input id="fatura-competencia-ate" type="date" value={form.competenciaAte} onChange={(e) => setCampo("competenciaAte", e.target.value)} style={fieldInputStyle} disabled={salvando} />
            </FormGroup>
          </Col>
        </Row>
        {!competenciaOk ? <Aviso tom="aviso">A competência inicial não pode ser depois da final.</Aviso> : null}
        {erroCriar ? <Aviso tom="erro">{erroCriar}</Aviso> : null}
      </div>
      <div className="docs-frete__modal-rodape">
        <Button color="secondary" size="sm" onClick={fechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button color="primary" size="sm" onClick={criar} disabled={!podeCriar} title={selecao.ids.length ? undefined : "Marque ao menos um documento"}>
          {salvando ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
          {salvando ? "Criando..." : `Criar fatura${selecao.ids.length ? ` com ${fmtNumber(selecao.ids.length)} documento(s)` : ""}`}
        </Button>
      </div>
    </ModalHub>
  );
}
